/* -----------------Helpers & Hooks--------------- */
import { useTranslation } from "react-i18next";
/* -----------------Types--------------- */
import type { SessionState } from "../../hooks/types";

import { events } from "@constants/tracking";
import { isAiSubmittable } from "@features/session/constants";
import { useAppStore } from "@hooks/useAppStore";
import type { DailyProgressState } from "@hooks/useDailyProgressStore";
import { getCurrentDate, getPlatform } from "@memorang/helpers/src/common";
import { calcScore } from "@memorang/helpers/src/scoring/multichoice";
import type { Confidence } from "@memorang/types/src/session/common";
import type {
	AIFeedbackResponse,
	AIResponsePayload,
	Choice,
	ItemResponse,
	ItemResponseFormField,
	OriginalItemTypes,
} from "@memorang/types/src/session/new-session";
import { trackCustomEvent } from "analytics";
import { trackAnswerEvent } from "../../api/mutations";
import type { TrackAnswerEventRequest } from "../../api/types";
import { getNormalizedScore } from "../../helpers/ai-feedback";

const calculatePoints = ({
	itemType,
	totalPoints,
	itemPoints,
	isCorrect,
	answeredFormStates,
	aiFeedback,
}: {
	itemType?: OriginalItemTypes;
	totalPoints: number;
	itemPoints: number;
	isCorrect?: boolean;
	answeredFormStates?: Record<
		string,
		{
			response: string;
			isCorrect: boolean;
		}
	>;
	aiFeedback?: AIFeedbackResponse;
}) => {
	const baseFormula = (numPointsEarned: number) => {
		return Math.round((numPointsEarned / totalPoints) * itemPoints);
	};

	switch (itemType) {
		case "LEXICAL_CLOZE":
		case "MCQ":
		case "NOTICE_MCQ":
			return baseFormula(isCorrect ? 1 : 0);
		case "NOTE_COMPLETION": {
			const numEarnedPoints = Object.values(answeredFormStates || {}).reduce(
				(acc, curr) => {
					return acc + (curr.isCorrect ? 1 : 0);
				},
				0,
			);
			return baseFormula(numEarnedPoints);
		}
		case "STORY_TELLING":
		case "LONG_TURN":
		case "WRITING_EMAIL": {
			const numEarnedPoints = Object.values(
				aiFeedback?.subskillAssessments || {},
			).reduce((acc, curr) => {
				return acc + (curr.score ?? 0);
			}, 0);
			return baseFormula(numEarnedPoints);
		}
		default:
			return 0;
	}
};
const isSataAnswerCorrect = (
	choices: Choice[],
	selectedChoiceIds?: string[],
) => {
	const numRequiredAnswers = choices.filter(
		({ isCorrect }) => isCorrect,
	).length;
	const numCorrectSelectedChoiceIds = selectedChoiceIds
		? selectedChoiceIds.filter((selectedChoiceId) => {
				const selectedChoice = choices.find(
					(item) => item.id === selectedChoiceId,
				);
				return selectedChoice ? selectedChoice.isCorrect : false;
			}).length
		: 0;
	const numCorrectChoiceIds = choices.filter(
		({ isCorrect }) => isCorrect,
	).length;

	const isCorrect = numRequiredAnswers
		? numCorrectSelectedChoiceIds >= numRequiredAnswers &&
			selectedChoiceIds?.length === numCorrectSelectedChoiceIds
		: numCorrectChoiceIds === numCorrectSelectedChoiceIds;

	return isCorrect;
};

const getAnsweredFormStates = (
	fields: ItemResponseFormField[],
	currentFormStates?: Record<string, string>,
) => {
	return Object.fromEntries(
		Object.entries(currentFormStates || {}).map(([fieldId, fieldState]) => [
			fieldId,
			{
				response: fieldState,
				isCorrect:
					fieldState.toLocaleLowerCase() ===
					fields
						.find((field) => field.id === fieldId)
						?.expected_response?.toLocaleLowerCase(),
			},
		]),
	);
};
const isNoteCompletionAnswerCorrect = (
	fields: ItemResponseFormField[],
	currentFormStates?: Record<string, string>,
) => {
	const isCorrect = fields.every((field) => {
		const fieldState = currentFormStates?.[field.id] || "";
		return (
			fieldState?.toLocaleLowerCase() ===
			field.expected_response?.toLocaleLowerCase()
		);
	});
	return isCorrect;
};
const getShouldShowConfidencesSubmitButtons = (
	sessionState: SessionState,
	currentItemIndex: number,
) => {
	const {
		itemStates,
		answers,
		sessionItems,
		currentBlockIndex,
		isSummativeTest,
		formStates,
	} = sessionState;

	if (isSummativeTest) {
		return false;
	}
	const { id } = sessionItems[currentBlockIndex][currentItemIndex];

	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const response = currentItem.response;

	const currentItemState = itemStates?.get(id!);
	const currentAnswer = answers?.get(id!);

	const numMinChoicesRequired =
		response.type === "choice" ? response.minCorrectChoices : undefined;

	const selectedChoiceIds = currentItemState?.selectedChoiceIds || [];

	const getIsSelected = () => {
		switch (response?.type) {
			case "choice": {
				if (response.selectionType === "multiple") {
					return (
						numMinChoicesRequired &&
						selectedChoiceIds?.length >= numMinChoicesRequired
					);
				}
				return selectedChoiceIds?.length > 0;
			}
			case "form": {
				const requiredFieldIds =
					response.fields
						?.filter((field) => field.required ?? true)
						.map((field) => field.id) || [];
				const currentFormStates = formStates?.[id!];
				const isSelected = requiredFieldIds.every(
					(fieldId: string) => currentFormStates?.[fieldId],
				);
				return isSelected;
			}
			default:
				return selectedChoiceIds?.length > 0;
		}
	};
	const isSelected = getIsSelected();

	return isSelected && !currentAnswer;
};

const getNextButtonDisabled = ({
	selectedChoiceIds = [],
	response,
}: {
	selectedChoiceIds: string[];
	response: ItemResponse;
}) => {
	if (
		response?.type !== "choice" ||
		response.selectionType !== "multiple" ||
		!selectedChoiceIds.length
	) {
		return false;
	}

	// const isMinChoiceCountValid = !!(
	// 	minChoiceCount && selectedChoiceIds.length < minChoiceCount
	// );
	// const isMaxChoiceCountValid = !!(
	// 	maxChoiceCount && selectedChoiceIds.length < maxChoiceCount
	// );

	return false;
};
const getNextButtonStates = (
	sessionState: SessionState,
	currentItemIndex: number,
) => {
	const { t } = useTranslation();
	const { sessionItems, itemStates, inReviewMode, currentBlockIndex } =
		sessionState;

	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const { id, response } = currentItem;

	const numChoicesRequired =
		response.type === "choice" ? response.minCorrectChoices : undefined;
	const minChoiceCount = numChoicesRequired;
	const currentItemState = itemStates?.get(id!);

	const isSata =
		response?.type === "choice" && response.selectionType === "multiple";

	const selectedChoiceIds =
		currentItemState?.selectedChoiceIds ||
		currentItem.answer?.selectedChoiceIds ||
		[];

	const getIsSelected = () => {
		if (inReviewMode) {
			return true;
		}
		if (isSata) {
			return (
				selectedChoiceIds?.length >= (minChoiceCount || 0) ||
				currentItemState?.answered
			);
		}
		return (
			(selectedChoiceIds && selectedChoiceIds.length > 0) ||
			(currentOrders && currentOrders.length > 0) ||
			currentItemState?.answered
		);
	};

	const currentOrders = currentItemState?.currentOrders;

	const isSelected = getIsSelected();

	const disabled = getNextButtonDisabled({
		response: response!,
		selectedChoiceIds: currentItemState?.selectedChoiceIds || [],
	});
	return {
		text: isSelected
			? t("session.navigation.next")
			: t("session.navigation.skip"),
		showButtonNavSkip: !(isSelected || inReviewMode),
		disabled,
	};
};

const getShowButtonNavFinish = (
	{ answers, inReviewMode, currentBlockIndex, sessionItems }: SessionState,
	currentItemIndex: number,
) => {
	const currentBlock = sessionItems[currentBlockIndex];
	const currentItem = currentBlock[currentItemIndex];
	const currentAnswer = answers?.get(currentItem.id!);
	const sessionLength = currentBlock.length;

	return (
		(currentAnswer != null || inReviewMode) &&
		currentItemIndex === sessionLength - 1
	);
};

const calculateAndUpdateProgress = (state: SessionState, forward?: boolean) => {
	const {
		updateProgress,
		currentProgress,
		numGoal,
		isSummativeTest,
		blockStates,
		currentBlockIndex,
	} = state;

	const finalProgress = isSummativeTest
		? blockStates?.get(currentBlockIndex)?.currentProgress || 0
		: currentProgress;

	let nextProgress = 0;

	if (forward) {
		const newProgress = finalProgress + 1 / numGoal!;
		nextProgress = newProgress;
		updateProgress(newProgress);
	} else {
		const newProgress = finalProgress - 1 / numGoal!;
		updateProgress(newProgress);
	}
	return nextProgress;
};

const getAccumulatedPoints = (
	choices: Choice[],
	selectedChoiceIds?: string[],
) => {
	const selectedChoicesWithPoints = choices.filter((item) => {
		return selectedChoiceIds?.includes(item.id) && item.points != null;
	});

	const accumulatedPoints = selectedChoicesWithPoints.length
		? selectedChoicesWithPoints.reduce(
				(acc, item) => acc + (item.points || 0),
				0,
			)
		: undefined;
	return accumulatedPoints;
};

const handlePostAnswer = (
	{
		state,
		startTime,
		confidence,
		dailyProgressState,
		projectId,
		organizationId,
		examId,
		submitAnswer = true,
		aiFeedback,
		totalScore,
		slideshowDurationInSeconds,
		currentItemIndex,
		showButtonNavSkip,
		shouldCreateAnswerForAiItem,
	}: {
		state: SessionState;
		startTime: Date;
		confidence?: Confidence;
		dailyProgressState?: DailyProgressState;
		projectId: number;
		organizationId: number;
		examId?: string;
		submitAnswer?: boolean;
		aiFeedback?: AIFeedbackResponse;
		totalScore?: number;
		slideshowDurationInSeconds?: number;
		currentItemIndex: number;
		showButtonNavSkip?: boolean;
		shouldCreateAnswerForAiItem?: boolean;
	},
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
) => {
	const {
		sessionItems,
		itemStates,
		id: sessionId,
		updateAnswers,
		updateItemStates,
		updateAnswerEventInProgress,
		isRecommendedTask,
		updateItemAttempts,
		updateShowConfetti,
		currentBlockIndex,
		isSummativeTest,
		blockStates,
		collectionId,
		formStates,
		timeElapsed,
		sessionTime,
	} = state;

	const isImageChallengeApp = useAppStore.getState().app.isImageChallengeApp;

	const dailyProgressByData = dailyProgressState?.dailyProgressByData;
	const setDailyProgressByDate = dailyProgressState?.setDailyProgressByDate;

	const currentDate = getCurrentDate();
	const key = `${examId}-${currentDate}`;

	const currentDailyProgress = dailyProgressByData?.[key];

	const timeTaken = Math.min(
		Math.round((Date.now() - startTime.getTime()) / 1000),
		300,
	);

	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const { id: currentItemId } = currentItem;
	const response = currentItem.response;

	const itemPoints = response?.itemPoints ?? 0;
	const totalPoints = response?.totalPoints ?? 0;

	const choices = response.type === "choice" ? response.choices : [];

	const currentItemState = itemStates?.get(currentItemId!);

	const answerId = choices?.find((item) => item.isCorrect)?.id;
	const correctChoiceIds = choices
		?.filter((item) => item.isCorrect)
		.map((item) => item.id);

	const numDistractors = choices ? choices.length - 1 : 0;

	const correctAnswerOrders =
		response.type === "reorder"
			? response.correctResponse?.map((choice: { id: string }) => choice.id)
			: undefined;

	const isSequence = response?.type === "reorder";

	const selectedChoiceIds =
		currentItemState?.selectedChoiceIds ||
		currentItem.answer?.selectedChoiceIds ||
		[];

	const selectedChoiceItemId = selectedChoiceIds?.[0];

	const isNoteCompletion = response?.type === "form";

	const currentFormStates = formStates?.[currentItemId!];

	const getIsCorrect = () => {
		if (isSequence) {
			return correctAnswerOrders?.every((order: string) =>
				currentItemState?.currentOrders?.includes(order),
			);
		}
		if (response?.type === "choice" && response.selectionType === "multiple") {
			return isSataAnswerCorrect(choices!, selectedChoiceIds);
		}
		if (isNoteCompletion) {
			return isNoteCompletionAnswerCorrect(
				response.fields || [],
				currentFormStates,
			);
		}
		return selectedChoiceItemId === answerId;
	};
	const isCorrect = getIsCorrect();

	const hints = currentItemState?.hints?.length
		? currentItemState?.hints
		: null;
	const currentBlockState = blockStates?.get(currentBlockIndex);
	const markedItemsForReview = currentBlockState?.markedItemsForReview;
	const highlightedItems = currentBlockState?.highlightedItems;

	const { score } = isSummativeTest
		? { score: isCorrect ? 100 : 0 }
		: calcScore(
				confidence ?? 0,
				Boolean(isCorrect),
				timeTaken,
				slideshowDurationInSeconds ?? 0,
				currentItem,
			);

	if (currentDailyProgress && isRecommendedTask && score > 90) {
		const { numCompleted, numGoal } = currentDailyProgress;
		const updatedProgress = {
			[key]: {
				numCompleted: (numCompleted || 0) + 1,
				numGoal,
				status: "ACTIVE" as const,
			},
		};
		setDailyProgressByDate?.(updatedProgress);
	}

	const itemType = currentItem.metadata?.type;
	const isAiSubmittableResponse = isAiSubmittable(response.type);

	const answeredFormStates = getAnsweredFormStates(
		response.type === "form" ? response.fields || [] : [],
		currentFormStates,
	);
	const currentAnswer = {
		timeTaken,
		confidence: confidence ?? 0,
		correct: isCorrect,
		score,
		selectedChoiceIds,
		answeredFormStates,
	};

	if (isImageChallengeApp) {
		updateItemAttempts({
			itemId: `${currentItemId}`,
			answered: isCorrect,
			showTryAgain: !isCorrect,
			selectedChoiceId: selectedChoiceItemId,
			attempted: true,
			correct: isCorrect,
		});
		if (isCorrect) {
			updateAnswers(currentItemId!, currentAnswer);
			updateShowConfetti({ itemId: `${currentItemId}`, showConfetti: true });
		}
	} else if (
		!(isSummativeTest || showButtonNavSkip) ||
		(isAiSubmittableResponse && shouldCreateAnswerForAiItem)
	) {
		updateAnswers(currentItemId!, currentAnswer);
		updateItemStates({
			itemId: currentItemId!,
			selectedChoiceId: selectedChoiceItemId!,
			correctAnswerIds: correctChoiceIds,
			scores: [score],
			answered: true,
		});
	}

	if (!submitAnswer) {
		return;
	}
	updateAnswerEventInProgress(true);

	const aiResponse: AIResponsePayload = {
		feedback: aiFeedback?.explanation ?? "",
		subskillAssessments:
			aiFeedback?.subskillAssessments?.map((subskill) => ({
				...subskill,
				maxScore: totalScore!,
			})) ?? [],
		ai_run_id: aiFeedback?.runDetails?.runId ?? "",
		ai_thread_id: aiFeedback?.runDetails?.threadId ?? "",
		metadata: {
			assistantId: aiFeedback?.runDetails?.assistantId ?? "",
		},
		answer: aiFeedback?.answer ?? "",
	};

	const aiNormalizedScore = getNormalizedScore(
		aiFeedback?.score ?? 0,
		totalScore!,
	);

	const explicitPoints = itemPoints
		? calculatePoints({
				itemType,
				totalPoints,
				itemPoints,
				isCorrect,
				aiFeedback,
				answeredFormStates,
			})
		: null;

	const accumulatedPoints = getAccumulatedPoints(choices, selectedChoiceIds);

	const trackAnswerEventRequest: TrackAnswerEventRequest = {
		itemId: currentItemId!,
		itemVersionId: currentItem.versionId || 1,
		sessionId,
		collectionId,
		projectId,
		organizationId,
		sessionType: isSummativeTest ? "SUMMATIVE" : "FORMATIVE",
		itemDetails: {
			type: "ANSWER_EVENT",
			itemType: itemType!,
			variant: itemType!,
		},
		response: {
			selectedChoiceIds: selectedChoiceIds ?? [],
			confidence: confidence ?? 0,
			chosenAnswerId: selectedChoiceItemId!,
			correctness: Boolean(isCorrect),
		},
		performance: {
			timeTaken,
			score: isAiSubmittableResponse ? aiNormalizedScore : score || 0,
			xp: 0,
			points: explicitPoints ?? aiFeedback?.score ?? accumulatedPoints,
		},
		settings: {
			shuffled: false,
			numDistractors,
		},
		userInteraction: {
			marked: Boolean(
				currentItem.answer?.marked ||
					markedItemsForReview?.includes(currentItem.id!),
			),
			highlight:
				currentItem.answer?.highlight ||
				highlightedItems?.get(currentItem.id!) ||
				"",
			hints: hints || [],
			sessionTime: timeElapsed + (sessionTime || 0),
		},
		platform: getPlatform(),
		...(isAiSubmittableResponse && aiResponse ? { aiResponse } : {}),
	};

	trackCustomEvent({
		eventName: events.sessionItemResponseSubmitted,
		confidence: confidence ?? 0,
		itemId: currentItemId,
		sessionId,
		timeTaken,
		type: "formative",
		correctness: isCorrect ? "true" : "false",
		selectedChoiceIds: selectedChoiceIds?.join(","),
		itemType: currentItem.metadata?.type,
		aiFeedback: JSON.stringify(aiFeedback),
	});
	// if (isMocked) {
	// 	updateAnswerEventInProgress(false);
	// 	return;
	// }

	trackAnswerEvent(trackAnswerEventRequest).then(() => {
		updateAnswerEventInProgress(false);
	});
};

type ShowAiSubmitResponseButtonParams = {
	sessionState: SessionState;
	currentItemIndex: number;
	currentUserResponse?: unknown;
	currentAudioResponse?: unknown;
	shouldValidateAudio?: boolean;
};

export const getShowSubmitAnswerAIFeedbackButton = ({
	sessionState,
	currentItemIndex,
	currentUserResponse,
	currentAudioResponse,
	shouldValidateAudio,
}: ShowAiSubmitResponseButtonParams) => {
	const { answers, sessionItems, currentBlockIndex } = sessionState;
	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const currentAnswer = answers?.get(currentItem.id!);

	const response = currentItem.response;

	const isAiSubmittableResponse = isAiSubmittable(response.type);

	const baseShowCondition = !currentAnswer && isAiSubmittableResponse;

	const shouldShowSubmitAnswerAIFeedbackButton =
		(response.type === "reorder" && !currentAnswer) ||
		(baseShowCondition &&
			Boolean(currentUserResponse) &&
			(shouldValidateAudio ? Boolean(currentAudioResponse) : true));

	return {
		disabled: currentAnswer != null,
		show: shouldShowSubmitAnswerAIFeedbackButton,
	};
};

export {
	getShouldShowConfidencesSubmitButtons,
	getNextButtonStates,
	calculateAndUpdateProgress,
	getShowButtonNavFinish,
	handlePostAnswer,
};
